import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';

interface Engineer {
  id: string;
  name: string | null;
  email: string;
  organization_name?: string | null;
}

interface SendForReviewDialogProps {
  open: boolean;
  onClose: () => void;
  submittalId: string;
  fileId: string;
  projectId: string;
  submittalNumber: string;
}

export function SendForReviewDialog({
  open,
  onClose,
  submittalId,
  fileId,
  projectId,
  submittalNumber,
}: SendForReviewDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  // Fetch project scopes to get engineer contacts with emails
  const { data: scopesData, isLoading: scopesLoading } = useQuery({
    queryKey: ['project-scopes', projectId],
    queryFn: () => api.projectScopes.list(projectId) as Promise<{ scopes: any[] }>,
    enabled: open && !!projectId,
  });

  // Build a flat list of contacts with emails from scopes
  const engineers: Engineer[] = [];
  const seenIds = new Set<string>();

  if (scopesData?.scopes) {
    for (const scope of scopesData.scopes) {
      // Each scope has a `contacts` array with { contact_id, contact_name, contact_email }
      const contacts = scope.contacts || [];
      for (const c of contacts) {
        if (c.contact_id && c.contact_email && !seenIds.has(c.contact_id)) {
          seenIds.add(c.contact_id);
          engineers.push({
            id: c.contact_id,
            name: c.contact_name || null,
            email: c.contact_email,
            organization_name: scope.organization_name || null,
          });
        }
      }
      // Also check the legacy single-contact field contact_id/contact_name
      if (scope.contact_id && !seenIds.has(scope.contact_id)) {
        // We need an email — only from contacts array so skip if not there
      }
    }
  }

  const toggleEngineer = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one engineer');
      return;
    }

    setSending(true);
    try {
      const result = await api.submittals.annotations.sendForReview(submittalId, {
        file_id: fileId,
        engineer_contact_ids: Array.from(selectedIds),
        note: note.trim() || undefined,
      });

      const sentCount = result.sent_to?.length || 0;
      const errorCount = result.errors?.length || 0;

      if (sentCount > 0) {
        toast.success(
          `Annotated PDF sent to ${sentCount} engineer${sentCount !== 1 ? 's' : ''}.` +
          (errorCount > 0 ? ` (${errorCount} failed)` : '')
        );
      } else {
        toast.error('Failed to send to any engineers');
      }

      setNote('');
      setSelectedIds(new Set());
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send for review');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send for Review — {submittalNumber}</DialogTitle>
          <DialogDescription>
            The annotated PDF will be sent to the selected engineers. The
            original file is not modified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Engineer list */}
          <div className="space-y-2">
            <Label>Engineers (from project scopes)</Label>
            {scopesLoading && (
              <p className="text-sm text-muted-foreground">Loading contacts…</p>
            )}
            {!scopesLoading && engineers.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No contacts with email addresses found in project scopes.
              </p>
            )}
            <div className="max-h-48 overflow-y-auto space-y-1 rounded border p-2">
              {engineers.map((eng) => (
                <label
                  key={eng.id}
                  className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(eng.id)}
                    onChange={() => toggleEngineer(eng.id)}
                    className="h-3.5 w-3.5"
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-medium truncate block">
                      {eng.name || eng.email}
                    </span>
                    <span className="text-xs text-muted-foreground truncate block">
                      {eng.email}
                      {eng.organization_name ? ` · ${eng.organization_name}` : ''}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Optional note */}
          <div className="space-y-2">
            <Label htmlFor="review-note">Note (optional)</Label>
            <Textarea
              id="review-note"
              placeholder="Add a note to the engineers…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || selectedIds.size === 0 || scopesLoading}
          >
            <Send className="w-3 h-3 mr-1" />
            {sending ? 'Sending…' : `Send to ${selectedIds.size || '…'} engineer${selectedIds.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
